'use client'

import {
  createContext,
  useActionState,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import type {
  ActiveField,
  ErrorField,
  FormContextValue,
  InputRef,
  OnActivityParams,
  RegisterParams,
  SetRefParams,
  UseFormProps,
  ValidationParams,
} from './types'

export const useForm = ({
  action,
  initialState,
  onBlur = false,
  resetTime = 2000,
}: UseFormProps) => {
  const inputsRefs = useRef<InputRef>(new Map())
  const [formState, formAction, isPending] = useActionState(
    action,
    initialState
  )
  const [state, setState] = useState<'success' | 'error' | null>(null)
  const [activeFields, setActiveFields] = useState<ActiveField>(new Map())
  const [errors, setErrors] = useState<ErrorField>(new Map())
  const [isFormReady, setIsFormReady] = useState(false)

  // Initialize form fields only once
  useEffect(() => {
    if (inputsRefs.current.size === 0) return

    const newActiveFields = new Map() as ActiveField
    const newErrors = new Map() as ErrorField

    for (const [id, { required, message }] of inputsRefs.current.entries()) {
      newActiveFields.set(id, { state: false })
      newErrors.set(id, {
        state: required || false,
        message: message,
      })
    }

    setActiveFields(newActiveFields)
    setErrors(newErrors)
  }, [])

  const onActivity = useCallback((params: OnActivityParams) => {
    const { value, id } = params
    function checkFieldActivity(
      currentActivityState: boolean,
      inputValue: string
    ): boolean {
      if (currentActivityState) return true
      return inputValue !== ''
    }

    setActiveFields((prevActiveFields) => {
      const updatedActiveFields = new Map(prevActiveFields)
      const currentField = updatedActiveFields.get(id)
      updatedActiveFields.set(id, {
        state: checkFieldActivity(currentField?.state || false, value),
      })
      return updatedActiveFields
    })
  }, [])

  const onValidation = useCallback((params: ValidationParams) => {
    const { value, id, validation } = params
    const isValid = validation(value)

    setErrors((prevErrors) => {
      const updatedErrors = new Map(prevErrors)
      const currentErrorField = updatedErrors.get(id)

      updatedErrors.set(id, {
        message: currentErrorField?.message || '',
        state: !isValid,
      })
      return updatedErrors
    })
  }, [])

  const setRef = useCallback((params: SetRefParams): void => {
    const { id, data } = params
    if (inputsRefs.current.has(id)) return
    inputsRefs.current.set(id, data)
  }, [])

  const register = useCallback(
    (params: RegisterParams) => {
      const { id, required, message, validation = () => true } = params

      return {
        ref: (node: HTMLElement | null) => {
          if (inputsRefs.current.has(id) || !node) return
          inputsRefs.current.set(id, {
            node,
            required,
            message: message ?? `invalid ${id}`,
          })
        },
        onChange: ({ target }: { target: HTMLInputElement }) => {
          onActivity({ value: target.value, id })
          if (onBlur) return
          onValidation({ value: target.value, id, validation })
        },
        onBlur: ({ target }: { target: HTMLInputElement }) => {
          if (!onBlur) return
          onValidation({ value: target.value, id, validation })
        },
      }
    },
    [onBlur, onActivity, onValidation]
  )

  useEffect(() => {
    let timeout: NodeJS.Timeout
    if (formState?.status === undefined) return

    //Updates errors from server side validation
    if (formState.errors) {
      setErrors((prevErrors) => {
        const updatedErrors = new Map(prevErrors)
        for (const [id, { message, state }] of formState.errors.entries()) {
          updatedErrors.set(id, {
            message,
            state,
          })
        }

        return updatedErrors
      })
    }

    // Reset state after resetTime milliseconds
    if (formState.status >= 200) {
      setState(formState.status === 200 ? 'success' : 'error')

      timeout = setTimeout(() => {
        setIsFormReady(false)
        setState(null)
      }, resetTime)
    }

    return () => clearTimeout(timeout)
  }, [formState])

  useEffect(() => {
    if (!errors?.values()) {
      setIsFormReady(false)
      return
    }

    setIsFormReady(Array.from(errors.values()).every(({ state }) => !state))
  }, [errors])

  return {
    formState,
    formAction,
    register,
    setRef,
    onValidation,
    activeFields,
    errors,
    isFormReady,
    isFormPending: isPending,
    isFormSuccess: state === 'success',
    isFormError: state === 'error',
  }
}

export const FormContext = createContext<FormContextValue | null>(null)
export const useFormContext = () => {
  const context = useContext(FormContext)

  if (context === null) {
    throw new Error('useFormContext must be used within a FormProvider')
  }

  return context
}
